import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import RichTextEditor from "@/components/admin/RichTextEditor";
import RichText from "@/components/RichText";
import ImageUploader from "@/components/admin/ImageUploader";
import { publicUrl } from "@/lib/storage";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

type Category =
  | "say_it_in_60"
  | "weekend_huddle"
  | "business_of_womens_sports"
  | "athlete_spotlight"
  | "originals";
type ContentType = "video" | "original";
type Status = "draft" | "preview" | "published";

type Post = {
  id: string;
  category: Category;
  content_type: ContentType;
  status: Status;
  slug: string;
  title: string;
  subtitle: string | null;
  body: string | null;
  cover_image_path: string | null;
  thumbnail_url: string | null;
  video_url: string | null;
  youtube_video_id: string | null;
  tags: string[];
  author: string | null;
  featured: boolean;
  published: boolean;
  published_at: string | null;
  sort_order: number;
};

type Editing = Partial<Post> & { id?: string };

type NewsRow = {
  id: string;
  title: string;
  url: string;
  summary: string | null;
  published_at: string | null;
  source_id: string;
  news_sources: { name: string } | null;
};

const CATEGORY_OPTIONS: { value: Category; label: string }[] = [
  { value: "say_it_in_60", label: "Say It In 60" },
  { value: "weekend_huddle", label: "Weekend Huddle" },
  { value: "business_of_womens_sports", label: "Business of Women's Sports" },
  { value: "athlete_spotlight", label: "Athlete Spotlight" },
  { value: "originals", label: "Originals" },
];

const labelFor = (c: Category) => CATEGORY_OPTIONS.find((o) => o.value === c)?.label ?? c;

const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 80);

const empty = (type: ContentType): Editing => ({
  category: type === "original" ? "originals" : "say_it_in_60",
  content_type: type,
  status: "draft",
  slug: "",
  title: "",
  subtitle: "",
  body: "",
  cover_image_path: null,
  video_url: "",
  tags: [],
  author: "",
  featured: false,
  published_at: null,
  sort_order: 0,
});

const STATUS_STYLES: Record<Status, string> = {
  published: "bg-primary text-primary-foreground",
  preview: "bg-secondary text-secondary-foreground",
  draft: "bg-muted text-muted-foreground",
};

const AdminMedia = () => {
  const [tab, setTab] = useState<"video" | "original" | "news">("video");
  const [list, setList] = useState<Post[]>([]);
  const [news, setNews] = useState<NewsRow[]>([]);
  const [sources, setSources] = useState<{ id: string; name: string }[]>([]);
  const [newsSource, setNewsSource] = useState("all");
  const [newsTopic, setNewsTopic] = useState("");
  const [editing, setEditing] = useState<Editing | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [sync, setSync] = useState<any>(null);
  const [filter, setFilter] = useState<Category | "all">("all");

  const load = async () => {
    setLoading(true);
    const [posts, syncState] = await Promise.all([
      supabase
        .from("media_posts")
        .select("*")
        .order("sort_order")
        .order("published_at", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false }),
      supabase.from("media_sync_state").select("*").eq("provider", "youtube").maybeSingle(),
    ]);
    if (posts.error) toast.error(posts.error.message);
    setList((posts.data as any) ?? []);
    setSync(syncState.data ?? null);
    setLoading(false);
  };

  const loadNews = async () => {
    const [items, srcs] = await Promise.all([
      supabase
        .from("news_items")
        .select("id, title, url, summary, published_at, source_id, news_sources(name)")
        .order("published_at", { ascending: false, nullsFirst: false })
        .limit(100),
      supabase.from("news_sources").select("id, name").order("name"),
    ]);
    setNews((items.data as any) ?? []);
    setSources((srcs.data as any) ?? []);
  };

  useEffect(() => {
    load();
    loadNews();
  }, []);

  const runSync = async () => {
    setSyncing(true);
    const { data, error } = await supabase.functions.invoke("youtube-sync", { body: {} });
    setSyncing(false);
    if (error) {
      const msg = (data as any)?.error ?? error.message;
      return toast.error(`Sync failed: ${msg}`);
    }
    const res = data as any;
    toast.success(
      res.imported > 0
        ? `${res.imported} new video${res.imported === 1 ? "" : "s"} imported as drafts.`
        : "Already up to date — no new videos.",
    );
    load();
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.title) return toast.error("Title required");
    if (!editing.category) return toast.error("Category required");
    const slug = (editing.slug || slugify(editing.title || "")).trim();
    if (!slug) return toast.error("Slug required");
    const status = (editing.status ?? "draft") as Status;
    const payload: any = {
      category: editing.category,
      content_type: editing.content_type ?? "original",
      status,
      slug,
      title: editing.title,
      subtitle: editing.subtitle || null,
      body: editing.body || null,
      cover_image_path: editing.cover_image_path ?? null,
      video_url: editing.video_url?.trim() || null,
      tags: editing.tags ?? [],
      author: editing.author?.trim() || null,
      featured: !!editing.featured,
      published_at:
        status === "published" && !editing.published_at
          ? new Date().toISOString()
          : editing.published_at ?? null,
      sort_order: editing.sort_order ?? 0,
    };
    const q = editing.id
      ? supabase.from("media_posts").update(payload).eq("id", editing.id)
      : supabase.from("media_posts").insert(payload);
    const { error } = await q;
    if (error) return toast.error(error.message);
    toast.success("Saved");
    setEditing(null);
    setPreviewing(false);
    load();
  };

  const setStatus = async (p: Post, status: Status) => {
    const patch: any = { status };
    if (status === "published" && !p.published_at) patch.published_at = new Date().toISOString();
    const { error } = await supabase.from("media_posts").update(patch).eq("id", p.id);
    if (error) return toast.error(error.message);
    toast.success(status === "published" ? "Published" : status === "preview" ? "Moved to preview" : "Unpublished");
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this item? This cannot be undone.")) return;
    const { error } = await supabase.from("media_posts").delete().eq("id", id);
    if (error) return toast.error(error.message);
    load();
  };

  const visible = useMemo(() => {
    const byType = list.filter((p) => p.content_type === (tab === "original" ? "original" : "video"));
    return filter === "all" ? byType : byType.filter((p) => p.category === filter);
  }, [list, tab, filter]);

  const visibleNews = useMemo(() => {
    const topic = newsTopic.trim().toLowerCase();
    return news.filter(
      (n) =>
        (newsSource === "all" || n.source_id === newsSource) &&
        (!topic ||
          n.title.toLowerCase().includes(topic) ||
          (n.summary ?? "").toLowerCase().includes(topic)),
    );
  }, [news, newsSource, newsTopic]);

  return (
    <AdminLayout>
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl">Content Studio</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {list.filter((p) => p.content_type === "video").length} videos ·{" "}
            {list.filter((p) => p.content_type === "original").length} originals · {news.length} news links
          </p>
        </div>
        {tab !== "news" && (
          <button
            onClick={() => {
              setPreviewing(false);
              setEditing(empty(tab === "original" ? "original" : "video"));
            }}
            className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-secondary transition-colors"
          >
            + New {tab === "original" ? "article" : "video"}
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {(["video", "original", "news"] as const).map((t) => (
          <button
            key={t}
            onClick={() => {
              setTab(t);
              setFilter("all");
            }}
            className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === t ? "bg-primary text-primary-foreground" : "bg-muted"}`}
          >
            {t === "video" ? "YouTube videos" : t === "original" ? "SIIS originals" : "News links"}
          </button>
        ))}
      </div>

      {tab === "video" && (
        <div className="rounded-2xl bg-card shadow-card p-4 mb-6 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px]">
            <p className="font-semibold text-sm">
              YouTube channel {sync?.channel_title ? `· ${sync.channel_title}` : `· ${sync?.channel_handle ?? "not set"}`}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {sync?.last_success_at
                ? `Last synced ${formatDistanceToNow(new Date(sync.last_success_at), { addSuffix: true })} · ${sync.last_imported_count} imported, ${sync.last_skipped_count} already here`
                : "Never synced yet."}
              {sync?.last_status === "error" && sync?.last_error ? ` · Last attempt failed: ${sync.last_error}` : ""}
            </p>
          </div>
          <button
            onClick={runSync}
            disabled={syncing}
            className="px-5 py-2.5 rounded-full bg-secondary text-secondary-foreground font-semibold disabled:opacity-60"
          >
            {syncing ? "Syncing…" : "Sync YouTube now"}
          </button>
        </div>
      )}

      {tab !== "news" && (
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setFilter("all")}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold ${filter === "all" ? "bg-primary text-primary-foreground" : "bg-muted"}`}
          >
            All series
          </button>
          {CATEGORY_OPTIONS.map((c) => (
            <button
              key={c.value}
              onClick={() => setFilter(c.value)}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold ${filter === c.value ? "bg-primary text-primary-foreground" : "bg-muted"}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      {tab === "news" ? (
        <div>
          <div className="flex flex-wrap gap-3 mb-5">
            <select
              value={newsSource}
              onChange={(e) => setNewsSource(e.target.value)}
              className={inp + " max-w-xs"}
              aria-label="Filter by source"
            >
              <option value="all">All sources</option>
              {sources.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <input
              value={newsTopic}
              onChange={(e) => setNewsTopic(e.target.value)}
              placeholder="Filter by topic or keyword"
              className={inp + " max-w-xs"}
              aria-label="Filter by topic"
            />
          </div>
          <div className="grid gap-3">
            {visibleNews.map((n) => (
              <div key={n.id} className="bg-card rounded-2xl p-4 shadow-card">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-primary">
                  {n.news_sources?.name ?? "Unknown source"}
                </p>
                <a
                  href={n.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold hover:text-primary block mt-1"
                >
                  {n.title}
                </a>
                {n.summary && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{n.summary}</p>}
                <p className="text-xs text-muted-foreground mt-2">
                  {n.published_at ? new Date(n.published_at).toLocaleDateString() : "No date"} · headline and excerpt
                  only, links to the source
                </p>
              </div>
            ))}
            {visibleNews.length === 0 && <p className="text-muted-foreground">No news links match those filters.</p>}
          </div>
        </div>
      ) : loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : (
        <div className="grid gap-3">
          {visible.map((p) => (
            <div key={p.id} className="bg-card rounded-2xl p-4 shadow-card">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <p className="font-semibold">{p.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {labelFor(p.category)}
                    {p.youtube_video_id ? " · from YouTube" : ""}
                    {p.featured ? " · Featured" : ""}
                  </p>
                </div>
                <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${STATUS_STYLES[p.status]}`}>
                  {p.status}
                </span>
                {p.status !== "published" ? (
                  <button onClick={() => setStatus(p, "published")} className="px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold">
                    Publish
                  </button>
                ) : (
                  <button onClick={() => setStatus(p, "draft")} className="px-3 py-1.5 rounded-full bg-muted text-sm font-semibold">
                    Unpublish
                  </button>
                )}
                <button
                  onClick={() => {
                    setPreviewing(false);
                    setEditing(p);
                  }}
                  className="px-3 py-1.5 rounded-full bg-muted text-sm font-semibold"
                >
                  Edit
                </button>
                <button onClick={() => remove(p.id)} className="text-sm font-semibold text-destructive">
                  Delete
                </button>
              </div>
            </div>
          ))}
          {visible.length === 0 && <p className="text-muted-foreground">Nothing here yet.</p>}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-card rounded-[1.5rem] shadow-bold max-w-2xl w-full p-6 my-8">
            <div className="flex justify-between items-center mb-4 gap-3">
              <h2 className="font-display text-2xl">
                {previewing ? "Preview" : `${editing.id ? "Edit" : "New"} ${editing.content_type === "original" ? "article" : "video"}`}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPreviewing((v) => !v)}
                  className="px-3 py-1.5 rounded-full bg-muted text-sm font-semibold"
                >
                  {previewing ? "Back to editing" : "Preview"}
                </button>
                <button
                  onClick={() => {
                    setEditing(null);
                    setPreviewing(false);
                  }}
                  aria-label="Close"
                  className="text-muted-foreground hover:text-foreground px-2"
                >
                  ✕
                </button>
              </div>
            </div>

            {previewing ? (
              <article className="space-y-4">
                {editing.cover_image_path && (
                  <img
                    src={publicUrl(editing.cover_image_path) ?? ""}
                    alt=""
                    className="w-full rounded-xl aspect-video object-cover"
                  />
                )}
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                  {labelFor((editing.category ?? "originals") as Category)}
                </p>
                <h3 className="font-display text-3xl leading-tight">{editing.title}</h3>
                {editing.subtitle && <p className="text-muted-foreground">{editing.subtitle}</p>}
                {editing.author && <p className="text-xs text-muted-foreground">By {editing.author}</p>}
                <RichText html={editing.body ?? ""} />
                <p className="text-xs text-muted-foreground">
                  This preview is only visible to admins until the status is set to Published.
                </p>
              </article>
            ) : (
              <div className="space-y-4">
                <Field label="Series / category *">
                  <select
                    value={editing.category ?? "say_it_in_60"}
                    onChange={(e) => setEditing({ ...editing, category: e.target.value as Category })}
                    className={inp}
                  >
                    {CATEGORY_OPTIONS.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Title *">
                  <input
                    value={editing.title ?? ""}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        title: e.target.value,
                        slug: editing.id || editing.slug ? editing.slug : slugify(e.target.value),
                      })
                    }
                    className={inp}
                    maxLength={200}
                  />
                </Field>
                <Field label="Dek / subtitle">
                  <input
                    value={editing.subtitle ?? ""}
                    onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })}
                    className={inp}
                    maxLength={300}
                  />
                </Field>
                <Field label="Slug *">
                  <input
                    value={editing.slug ?? ""}
                    onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })}
                    className={inp}
                  />
                </Field>
                {editing.content_type === "video" && (
                  <Field label="Video URL (YouTube, Vimeo, or .mp4)">
                    <input
                      value={editing.video_url ?? ""}
                      onChange={(e) => setEditing({ ...editing, video_url: e.target.value })}
                      className={inp}
                      placeholder="https://youtube.com/watch?v=..."
                    />
                  </Field>
                )}
                <Field label={editing.content_type === "original" ? "Body" : "Description"}>
                  <RichTextEditor value={editing.body ?? ""} onChange={(v) => setEditing({ ...editing, body: v })} />
                </Field>
                <Field label={editing.content_type === "original" ? "Hero image" : "Cover image"}>
                  <ImageUploader
                    value={editing.cover_image_path ?? null}
                    onChange={(p) => setEditing({ ...editing, cover_image_path: p })}
                    prefix="media-covers"
                  />
                  {!editing.cover_image_path && editing.thumbnail_url && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Using the YouTube thumbnail until you upload one.
                    </p>
                  )}
                </Field>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Author">
                    <input
                      value={editing.author ?? ""}
                      onChange={(e) => setEditing({ ...editing, author: e.target.value })}
                      className={inp}
                      maxLength={120}
                    />
                  </Field>
                  <Field label="Tags (comma separated)">
                    <input
                      value={(editing.tags ?? []).join(", ")}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          tags: e.target.value
                            .split(",")
                            .map((t) => t.trim())
                            .filter(Boolean)
                            .slice(0, 12),
                        })
                      }
                      className={inp}
                    />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Status">
                    <select
                      value={editing.status ?? "draft"}
                      onChange={(e) => setEditing({ ...editing, status: e.target.value as Status })}
                      className={inp}
                    >
                      <option value="draft">Draft (admin only)</option>
                      <option value="preview">Preview (admin only)</option>
                      <option value="published">Published (public)</option>
                    </select>
                  </Field>
                  <Field label="Publish date">
                    <input
                      type="date"
                      value={editing.published_at ? String(editing.published_at).slice(0, 10) : ""}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          published_at: e.target.value ? new Date(e.target.value).toISOString() : null,
                        })
                      }
                      className={inp}
                    />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Sort order">
                    <input
                      type="number"
                      value={editing.sort_order ?? 0}
                      onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) || 0 })}
                      className={inp}
                    />
                  </Field>
                  <label className="flex items-center gap-3 px-4 py-3 rounded-xl bg-muted cursor-pointer mt-6">
                    <input
                      type="checkbox"
                      checked={!!editing.featured}
                      onChange={(e) => setEditing({ ...editing, featured: e.target.checked })}
                      className="w-5 h-5 accent-primary"
                    />
                    <span className="font-semibold text-sm">Featured</span>
                  </label>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setEditing(null);
                  setPreviewing(false);
                }}
                className="px-5 py-2.5 rounded-full bg-muted font-semibold"
              >
                Cancel
              </button>
              <button onClick={save} className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

const inp = "w-full px-3 py-2 rounded-xl bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-primary text-sm";
const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wider">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

export default AdminMedia;
